import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BbTask,
  BbTaskLadder,
  bbDeleteTaskLadder,
  bbGetTasks,
  bbUpsertTaskLadder,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { TIER_SLUGS } from '../shared/tierSlugs';

export function SectionTradingTiers({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [tasks, setTasks] = useState<BbTask[]>([]);
  const [ladderDlg, setLadderDlg] = useState<{
    task: BbTask;
    ladder?: BbTaskLadder;
  } | null>(null);
  const [lform, setLform] = useState<Partial<BbTaskLadder>>({});

  const load = useCallback(() => {
    bbGetTasks()
      .then(setTasks)
      .catch(() => setTasks([]));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const tierTasks = useMemo(() => tasks.filter((x) => TIER_SLUGS.includes(x.slug)), [tasks]);
  const saveLadder = async () => {
    if (!ladderDlg) return;
    try {
      // 如果当前 ladder 勾选了 Top，则自动取消同任务下其他 Top（保证唯一 Top）
      if (lform.isTopTier) {
        const currentId = ladderDlg.ladder?.id;
        const otherTop = (ladderDlg.task.ladders || []).filter(
          (x) => x.isTopTier && x.id !== currentId
        );
        await Promise.all(
          otherTop.map((l) =>
            bbUpsertTaskLadder({
            id: l.id,
            taskId: ladderDlg.task.id,
            volumeThreshold: l.volumeThreshold,
            rewardBox: l.rewardBox,
            isTopTier: false,
            displayOrder: l.displayOrder,
            })
          )
        );
      }
      await bbUpsertTaskLadder({
        id: ladderDlg.ladder?.id,
        taskId: ladderDlg.task.id,
        volumeThreshold: lform.volumeThreshold!,
        rewardBox: lform.rewardBox!,
        isTopTier: !!lform.isTopTier,
        displayOrder: lform.displayOrder!,
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setLadderDlg(null);
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const delLadder = async (id: number) => {
    if (!window.confirm(t('module.blindBox.confirmDelete'))) return;
    try {
      await bbDeleteTaskLadder(id);
      notify(t('module.blindBox.deleteOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  return (
    <Stack spacing={3}>
      {tierTasks.map((task) => (
        <Box key={task.id}>
          <Typography variant="subtitle2" color="text.secondary">
            {task.name} ({task.slug})
          </Typography>
          <PermissionButton resource="bb-trading-tiers" action="update">
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setLadderDlg({ task });
                setLform({
                  volumeThreshold: '0',
                  rewardBox: 1,
                  // 交易量阶梯任务新增档位默认标记为 Top（保存时会自动取消同任务下其他 Top）
                  isTopTier: TIER_SLUGS.includes(task.slug),
                  displayOrder: (task.ladders?.length || 0) + 1,
                });
              }}
            >
              {t('module.blindBox.addTier')}
            </Button>
          </PermissionButton>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>threshold</TableCell>
                  <TableCell>rewardBox</TableCell>
                  <TableCell>top</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(task.ladders || [])
                  .slice()
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.volumeThreshold}</TableCell>
                      <TableCell>{l.rewardBox}</TableCell>
                      <TableCell>{l.isTopTier ? 'Y' : 'N'}</TableCell>
                      <TableCell align="right">
                        <PermissionButton resource="bb-trading-tiers" action="update">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setLadderDlg({ task, ladder: l });
                              setLform(l);
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </PermissionButton>
                        <PermissionButton resource="bb-trading-tiers" action="delete">
                          <IconButton size="small" onClick={() => delLadder(l.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </PermissionButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      <Dialog open={!!ladderDlg} onClose={() => setLadderDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('module.blindBox.addTier')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="volumeThreshold (USDC)"
              value={lform.volumeThreshold || ''}
              onChange={(e) => setLform({ ...lform, volumeThreshold: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="rewardBox"
              value={lform.rewardBox ?? 0}
              onChange={(e) => setLform({ ...lform, rewardBox: parseInt(e.target.value, 10) || 0 })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!lform.isTopTier}
                  onChange={(e) => setLform({ ...lform, isTopTier: e.target.checked })}
                />
              }
              label="isTopTier"
            />
            {/*<TextField*/}
            {/*  type="number"*/}
            {/*  label="displayOrder"*/}
            {/*  value={lform.displayOrder ?? 0}*/}
            {/*  onChange={(e) =>*/}
            {/*    setLform({ ...lform, displayOrder: parseInt(e.target.value, 10) || 0 })*/}
            {/*  }*/}
            {/*  fullWidth*/}
            {/*/>*/}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLadderDlg(null)}>{t('actions.cancel')}</Button>
          <PermissionButton resource="bb-trading-tiers" action="update">
            <Button variant="contained" onClick={saveLadder}>
              {t('actions.save')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
